import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  MapPin, Calendar, Building2, Shield, Truck,
  Weight, Clock, ArrowRight, Package, XCircle, RefreshCw, Pencil, CheckCircle
} from "lucide-react";
import { formatRelativeTime } from "@/lib/utils";
import { useLocation } from "wouter";
import type { Truck as TruckType } from "@shared/schema";
import MemberLink from "./MemberLink";
import ShareLoadDialog from "./ShareLoadDialog";
import EditTruckModal from "./EditTruckModal";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PublicUserInfo {
  id: string;
  company: string;
  companyLogo?: string | null;
  dotNumber?: string;
  mcNumber?: string;
  adminVerified: boolean;
  upvoteCount: number;
  completedShipments: number;
  userType: string;
  lastActiveAt?: string | null;
  lastCapacityUpdate?: string | null;
}

interface TruckWithCarrier extends TruckType {
  carrier: PublicUserInfo | null;
}

interface TruckDetailModalProps {
  truck: TruckWithCarrier | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId?: string;
  currentUserType?: string;
  currentUserVerified?: boolean;
  onTruckStatusChange?: () => void;
}

export default function TruckDetailModal({ 
  truck, 
  open, 
  onOpenChange, 
  currentUserId,
  currentUserType,
  currentUserVerified = false,
  onTruckStatusChange
}: TruckDetailModalProps) {
  const [, setLocation] = useLocation();
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [confirmCloseTruckOpen, setConfirmCloseTruckOpen] = useState(false);
  const { toast } = useToast();

  const closeTruckMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/trucks/${truck?.id}/close`);
    },
    onSuccess: () => {
      toast({ title: "Truck space closed", description: "This truck posting is no longer visible to shippers." });
      queryClient.invalidateQueries({ queryKey: ['/api/trucks'] });
      onTruckStatusChange?.();
      onOpenChange(false);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to close truck posting.", variant: "destructive" });
    }
  });

  const reactivateTruckMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/trucks/${truck?.id}/reactivate`);
    },
    onSuccess: () => {
      toast({ title: "Truck space reactivated", description: "This truck posting is now visible to shippers again." });
      queryClient.invalidateQueries({ queryKey: ['/api/trucks'] });
      onTruckStatusChange?.();
      onOpenChange(false);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to reactivate truck posting.", variant: "destructive" });
    }
  });

  if (!truck) return null;

  const isOwnTruck = truck.userId === currentUserId;
  const isShipperOrBroker = currentUserType === "Shipper" || currentUserType === "Broker";
  const canShareLoads = currentUserId && !isOwnTruck && truck.carrier && isShipperOrBroker;
  const isClosed = truck.status === "Closed";
  
  const formatLocation = (origin: string | null, originCity: string | null, originState: string | null, originZip: string | null) => {
    if (originCity && originState && originZip) {
      return `${originCity}, ${originState} ${originZip}`;
    }
    if (originCity && originState) {
      return `${originCity}, ${originState}`;
    }
    return origin || "Not specified";
  };

  const originDisplay = formatLocation(truck.origin, truck.originCity, truck.originState, truck.originZip);
  const destinationDisplay = formatLocation(truck.destination, truck.destinationCity, truck.destinationState, truck.destinationZip);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">

        {/* Fixed header */}
        <div className="shrink-0 px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="modal-title">
              <Truck className="w-5 h-5" />
              Truck Space Details
            </DialogTitle>
            <DialogDescription>
              {originDisplay} → {destinationDisplay}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">

            {/* Carrier Info */}
            {truck.carrier && (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  {currentUserVerified || isOwnTruck ? (
                    <MemberLink 
                      userId={truck.carrier.id} 
                      name={truck.carrier.company}
                      companyLogo={truck.carrier.companyLogo}
                      showAvatar
                      avatarSize="md"
                      className="font-medium"
                      data-testid="link-carrier-company"
                    />
                  ) : (
                    <p className="font-medium text-muted-foreground" data-testid="text-carrier-restricted">
                      Carrier (verify your account to view details)
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                    <Badge variant="secondary" className="text-xs">{truck.carrier.userType}</Badge>
                    {truck.carrier.adminVerified && (
                      <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                    {(currentUserVerified || isOwnTruck) && truck.carrier.dotNumber && (
                      <span>DOT: {truck.carrier.dotNumber}</span>
                    )}
                    {(currentUserVerified || isOwnTruck) && truck.carrier.mcNumber && (
                      <span>MC: {truck.carrier.mcNumber}</span>
                    )}
                    {(currentUserVerified || isOwnTruck) && truck.carrier.completedShipments > 0 && (
                      <span>{truck.carrier.completedShipments} shipments</span>
                    )}
                    {(currentUserVerified || isOwnTruck) && truck.carrier.upvoteCount > 0 && (
                      <span>+{truck.carrier.upvoteCount} upvotes</span>
                    )}
                    {(currentUserVerified || isOwnTruck) && formatRelativeTime(truck.carrier.lastActiveAt) && (
                      <span className="flex items-center gap-1" data-testid="text-carrier-last-active">
                        <Clock className="w-3 h-3" />
                        Active {formatRelativeTime(truck.carrier.lastActiveAt)}
                      </span>
                    )}
                    {(currentUserVerified || isOwnTruck) && formatRelativeTime(truck.lastConfirmedAt) && (
                      <span className="flex items-center gap-1" data-testid="text-truck-last-confirmed">
                        <CheckCircle className="w-3 h-3" />
                        Confirmed {formatRelativeTime(truck.lastConfirmedAt)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Route */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Route
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 text-green-600 mb-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm font-medium">Origin</span>
                  </div>
                  <p className="font-medium" data-testid="text-truck-origin">{originDisplay}</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 text-red-600 mb-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm font-medium">Destination</span>
                  </div>
                  <p className="font-medium" data-testid="text-truck-destination">{destinationDisplay}</p>
                </div>
              </div>
            </div>

            {/* Capacity & Equipment */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Truck Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Weight className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Capacity</span>
                  </div>
                  <p className="text-lg font-bold" data-testid="text-truck-capacity">
                    {truck.capacity.toLocaleString()} lbs
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Truck className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Equipment</span>
                  </div>
                  <p className="font-medium" data-testid="text-truck-equipment">
                    Dry Van
                  </p>
                </div>
              </div>
            </div>

            {/* Availability */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Availability
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Available From</p>
                    <p className="font-medium" data-testid="text-available-from">
                      {truck.availableFrom ? new Date(truck.availableFrom).toLocaleDateString() : "Immediately"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Available Until</p>
                    <p className="font-medium" data-testid="text-available-until">
                      {truck.availableTo ? new Date(truck.availableTo).toLocaleDateString() : "Flexible"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Badge 
                variant={truck.status === "Available" ? "default" : "secondary"}
                data-testid="badge-truck-status"
              >
                {truck.status}
              </Badge>
            </div>

            {/* Notes */}
            {truck.description && (currentUserVerified || isOwnTruck) && (
              <div className="space-y-2">
                <h3 className="font-semibold">Carrier Notes</h3>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg" data-testid="text-truck-notes">
                  {truck.description}
                </p>
              </div>
            )}

          </div>
        </div>

        {/* Fixed footer — action buttons always visible */}
        <div className="border-t border-border px-6 py-4 bg-background shrink-0">
          <div className="flex gap-3 flex-wrap">
            {/* Owner actions */}
            {isOwnTruck && (
              <>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowEditModal(true)}
                  data-testid="button-edit-truck"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                {isClosed ? (
                  <Button 
                    className="flex-1"
                    onClick={() => reactivateTruckMutation.mutate()}
                    disabled={reactivateTruckMutation.isPending}
                    data-testid="button-reactivate-truck"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${reactivateTruckMutation.isPending ? 'animate-spin' : ''}`} />
                    {reactivateTruckMutation.isPending ? "Reactivating..." : "Reactivate Truck Space"}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => setConfirmCloseTruckOpen(true)}
                      disabled={closeTruckMutation.isPending}
                      data-testid="button-close-truck"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      {closeTruckMutation.isPending ? "Closing..." : "Close Truck Space"}
                    </Button>
                    <AlertDialog open={confirmCloseTruckOpen} onOpenChange={setConfirmCloseTruckOpen}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Close this truck space?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action removes this truck from the marketplace — shippers and brokers will no longer be able to find it.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="button-cancel-close-truck">Keep Listing</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => { setConfirmCloseTruckOpen(false); closeTruckMutation.mutate(); }}
                            data-testid="button-confirm-close-truck"
                          >
                            Close Truck Space
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </>
            )}
            {canShareLoads && currentUserVerified && (
              <Button 
                className="flex-1"
                onClick={() => setShowShareDialog(true)}
                data-testid="button-share-load"
              >
                <Package className="w-4 h-4 mr-2" />
                Share Load Details with Carrier
              </Button>
            )}
          </div>
        </div>

        {/* Share Load Dialog */}
        {canShareLoads && currentUserId && truck.carrier && (
          <ShareLoadDialog
            open={showShareDialog}
            onOpenChange={setShowShareDialog}
            carrierId={truck.userId}
            carrierName={truck.carrier.company}
            currentUserId={currentUserId}
          />
        )}

        {/* Edit Truck Modal */}
        {isOwnTruck && (
          <EditTruckModal
            truck={truck}
            open={showEditModal}
            onOpenChange={setShowEditModal}
            onSuccess={() => {
              onTruckStatusChange?.();
              onOpenChange(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
